import { Box, type BoxProps } from "@mantine/core";

export const SimpleLine = ({
    width = "100%",
    color = "currentColor",
    ...props
}: BoxProps & { width?: string | number; color?: string }) => {
    return (
        <Box w={width} {...props}>
            <svg viewBox="0 0 100 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto" }}>
                {/* Playful wavy line */}
                <path
                    d="M0 10 Q 12 5, 25 10 Q 38 15, 50 10 Q 62 5, 75 10 Q 88 15, 100 10"
                    stroke={color}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    fill="none"
                    opacity="0.8"
                />
                {/* Small accent dots */}
                <circle cx="25" cy="10" r="1.5" fill={color} opacity="0.5" />
                <circle cx="75" cy="10" r="1.5" fill={color} opacity="0.5" />
            </svg>
        </Box>
    );
};

export const OrnamentalDivider = ({ color = "white", ...props }: BoxProps & { color?: string }) => {
    return (
        <Box w="100%" maw={400} {...props}>
            <svg viewBox="0 0 400 30" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto" }}>
                {/* Single continuous organic line with subtle center flourish */}
                <path
                    d="M10 15 Q 50 12, 100 15 T 180 14 Q 195 14, 200 8 Q 205 14, 220 14 T 300 15 Q 350 18, 390 15"
                    stroke={color}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    opacity="0.8"
                    fill="none"
                />
                {/* Small decorative dot at center */}
                <circle cx="200" cy="15" r="2" fill={color} opacity="0.7" />
            </svg>
        </Box>
    );
};

export const SideFlourish = ({ color = "white", flipped = false, ...props }: BoxProps & { color?: string; flipped?: boolean }) => {
    return (
        <Box w="100%" maw={100} style={{ transform: flipped ? "scaleX(-1)" : "none", marginTop: "0.65rem" }} {...props}>
            <svg viewBox="0 0 100 30" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto" }}>
                {/* Playful swirling flourish */}
                <path
                    d="M0 15 Q 20 10, 40 15 Q 55 20, 65 12 Q 75 4, 85 12 Q 92 18, 98 10"
                    stroke={color}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    fill="none"
                    opacity="0.8"
                />
                {/* Decorative spiral curl at end */}
                <path
                    d="M95 12 Q 100 8, 97 5 Q 93 3, 90 7"
                    stroke={color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    fill="none"
                    opacity="0.6"
                />
                {/* Small accent dot */}
                <circle cx="88" cy="8" r="1.5" fill={color} opacity="0.5" />
            </svg>
        </Box>
    );
};

export const ScrollIndicator = ({ color = "white", ...props }: BoxProps & { color?: string }) => {
    return (
        <Box {...props}>
            <style>
                {`
                    @keyframes scrollBounce {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(8px); }
                    }
                    @keyframes scrollDraw {
                        0% { stroke-dashoffset: 60; }
                        50% { stroke-dashoffset: 0; }
                        100% { stroke-dashoffset: -60; }
                    }
                    .scroll-indicator-path {
                        animation: scrollDraw 2s ease-in-out infinite;
                    }
                    .scroll-indicator-arrow {
                        animation: scrollBounce 1.5s ease-in-out infinite;
                    }
                `}
            </style>
            <svg viewBox="0 0 40 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "40px", height: "80px" }}>
                {/* Decorative wavy vertical line */}
                <path
                    className="scroll-indicator-path"
                    d="M20 5 Q 25 15, 20 25 Q 15 35, 20 45"
                    stroke={color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    fill="none"
                    opacity="0.6"
                    strokeDasharray="60"
                />
                {/* Bouncing arrow */}
                <g className="scroll-indicator-arrow">
                    <path
                        d="M12 55 Q 20 62, 20 65 Q 20 62, 28 55"
                        stroke={color}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        fill="none"
                        opacity="0.8"
                    />
                    <path
                        d="M14 62 Q 20 70, 20 73 Q 20 70, 26 62"
                        stroke={color}
                        strokeWidth="2"
                        strokeLinecap="round"
                        fill="none"
                        opacity="0.5"
                    />
                </g>
                {/* Small decorative dot */}
                <circle cx="20" cy="5" r="2" fill={color} opacity="0.7" />
            </svg>
        </Box>
    );
};
